/**
 * Folder & Tag Management API
 *
 * Folders and tag definitions are stored in KV alongside template data,
 * using the same content/bulk API.
 *
 * Key prefixes:
 *   __folder:{id}   → FolderDef
 *   __tagdef:{name}  → TagDef
 */

import { apiFetch } from "./apiClient";

/* ================================================================
   Types
   ================================================================ */

export interface FolderDef {
  id: string;
  name: string;
  /** hex color for folder icon/badge */
  color: string;
  /** Parent folder ID (empty string = root level) */
  parentId: string;
  /** Sort order within parent */
  order: number;
  createdAt: number;
}

export interface TagDef {
  /** Tag name (unique identifier) */
  name: string;
  /** Index into TAG_COLORS palette (0-9) */
  colorIndex: number;
  createdAt: number;
}

/* ================================================================
   Folder color palette — 10 distinctive folder colors
   ================================================================ */

export const FOLDER_COLORS: { hex: string; label: string }[] = [
  { hex: "#3b82f6", label: "\u05DB\u05D7\u05D5\u05DC" },
  { hex: "#8b5cf6", label: "\u05E1\u05D2\u05D5\u05DC" },
  { hex: "#ec4899", label: "\u05D5\u05E8\u05D5\u05D3" },
  { hex: "#ef4444", label: "\u05D0\u05D3\u05D5\u05DD" },
  { hex: "#f97316", label: "\u05DB\u05EA\u05D5\u05DD" },
  { hex: "#eab308", label: "\u05E6\u05D4\u05D5\u05D1" },
  { hex: "#22c55e", label: "\u05D9\u05E8\u05D5\u05E7" },
  { hex: "#14b8a6", label: "\u05D8\u05D5\u05E8\u05E7\u05D9\u05D6" },
  { hex: "#06b6d4", label: "\u05E6\u05D9\u05D0\u05DF" },
  { hex: "#64748b", label: "\u05D0\u05E4\u05D5\u05E8" },
];

/* ================================================================
   Key helpers
   ================================================================ */

const FOLDER_PREFIX = "__folder:";
const TAGDEF_PREFIX = "__tagdef:";

function folderKey(id: string) { return FOLDER_PREFIX + id; }
function tagDefKey(name: string) { return TAGDEF_PREFIX + name; }

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/* ================================================================
   Folder CRUD
   ================================================================ */

/** List all folders */
export async function listFolders(): Promise<FolderDef[]> {
  try {
    const data = await apiFetch("/content");
    const raw: Record<string, any> = data.content || {};
    const folders: FolderDef[] = [];
    for (const [key, value] of Object.entries(raw)) {
      if (key.startsWith(FOLDER_PREFIX) && value && typeof value === "object") {
        folders.push(value as FolderDef);
      }
    }
    return folders.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (err) {
    console.error("[folderTagApi] Failed to list folders:", err);
    return [];
  }
}

/** Create a new folder */
export async function createFolder(
  name: string,
  color: string,
  parentId = "",
  order = 0,
): Promise<FolderDef> {
  const id = generateId();
  const folder: FolderDef = {
    id,
    name,
    color,
    parentId,
    order,
    createdAt: Date.now(),
  };
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [folderKey(id)]: folder } }),
  });
  return folder;
}

/** Update a folder */
export async function updateFolder(
  id: string,
  updates: Partial<Omit<FolderDef, "id" | "createdAt">>,
): Promise<FolderDef> {
  const folders = await listFolders();
  const existing = folders.find((f) => f.id === id);
  if (!existing) throw new Error(`Folder not found: ${id}`);
  const updated = { ...existing, ...updates };
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [folderKey(id)]: updated } }),
  });
  return updated;
}

/** Delete a folder (templates in it become "unfoldered") */
export async function deleteFolder(id: string): Promise<void> {
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [folderKey(id)]: "" } }),
  });
}

/* ================================================================
   Tag Definition CRUD
   ================================================================ */

/** List all tag definitions */
export async function listTagDefs(): Promise<TagDef[]> {
  try {
    const data = await apiFetch("/content");
    const raw: Record<string, any> = data.content || {};
    const tags: TagDef[] = [];
    for (const [key, value] of Object.entries(raw)) {
      if (key.startsWith(TAGDEF_PREFIX) && value && typeof value === "object") {
        tags.push(value as TagDef);
      }
    }
    return tags.sort((a, b) => a.name.localeCompare(b.name, "he"));
  } catch (err) {
    console.error("[folderTagApi] Failed to list tag defs:", err);
    return [];
  }
}

/** Create or update a tag definition (upsert by name) */
export async function upsertTagDef(
  name: string,
  colorIndex: number,
): Promise<TagDef> {
  const existing = (await listTagDefs()).find((t) => t.name === name);
  const tagDef: TagDef = {
    name,
    colorIndex,
    createdAt: existing?.createdAt || Date.now(),
  };
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [tagDefKey(name)]: tagDef } }),
  });
  return tagDef;
}

/** Delete a tag definition */
export async function deleteTagDef(name: string): Promise<void> {
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [tagDefKey(name)]: "" } }),
  });
}
